import { ReactNode } from 'react';
import {
    PaginationButton,
    PaginationEllipses,
    PaginationStep,
    Pagination as SwanPagination,
} from '@vp/swan';
import { __ } from '@99designs/i18n';

type PaginationProps = {
    currentPage: number;
    totalPages: number;
    onChange: (page: number) => void;
};

/**
 * Pagination displays a controlled pagination component with dynamic ellipses and navigation buttons.
 *
 * @param currentPage The currently selected page.
 * @param totalPages The total number of pages.
 * @param handleChange Callback to update the current page.
 */

export function Pagination({ currentPage, totalPages, onChange }: PaginationProps) {
    const maxVisibleElements = 5; // total visible elements including ellipses
    let elements: ReactNode[] = [];

    function PaginationStepButton(page: number) {
        return (
            <PaginationStep
                key={`${page}-step`}
                as="button"
                onClick={(e) => {
                    e.preventDefault();
                    onChange(page);
                }}
                active={page === currentPage}
                accessibleText={__(`Page {{pageNumber}}`, {
                    pageNumber: page,
                    _context: 'Describes the page number, i.e. Page 1',
                })}
            >
                {page}
            </PaginationStep>
        );
    }

    // Show all elements if total pages is less than or equal to max visible
    if (totalPages <= maxVisibleElements) {
        elements = Array.from({ length: totalPages }, (_, i) => i + 1).map((page) =>
            PaginationStepButton(page)
        );
    }
    // Show condensed pagination with ellipses if total pages exceed max visible
    else {
        const showLeftEllipses = currentPage > 3;
        const showRightEllipses = currentPage < totalPages - 2;

        // Always show the first page
        elements.push(PaginationStepButton(1));

        if (showLeftEllipses) {
            elements.push(<PaginationEllipses key="left-ellipses" title={'left-ellipses'} />);
        }

        // Middle pages displayed between first and last page
        let middlePages: number[] = [];

        if (!showLeftEllipses && showRightEllipses) {
            middlePages = [2, 3];
        } else if (showLeftEllipses && showRightEllipses) {
            middlePages = [currentPage];
        } else if (showLeftEllipses && !showRightEllipses) {
            middlePages = [totalPages - 2, totalPages - 1];
        }

        // Add middle pages
        middlePages.forEach((page) => {
            if (page > 1 && page < totalPages) {
                elements.push(PaginationStepButton(page));
            }
        });

        if (showRightEllipses) {
            elements.push(<PaginationEllipses key="right-ellipses" title={'right-ellipses'} />);
        }

        // Always show the last page
        elements.push(PaginationStepButton(totalPages));
    }

    return (
        <SwanPagination accessibleText={__('Pagination navigation')}>
            <PaginationButton
                variant="previous"
                as="button"
                onClick={(e) => {
                    e.preventDefault();
                    onChange(currentPage - 1);
                }}
                disabled={currentPage <= 1}
                accessibleText={__('Previous pagination button')}
            />
            {elements}
            <PaginationButton
                variant="next"
                as="button"
                onClick={(e) => {
                    e.preventDefault();
                    onChange(currentPage + 1);
                }}
                disabled={currentPage >= totalPages}
                accessibleText={__('Next pagination button')}
            />
        </SwanPagination>
    );
}
